import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Activity, CalendarDays, FileText, Sparkles } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-sand-50 grain">
      <nav className="sticky top-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-serif text-2xl tracking-tight text-stone-900" data-testid="brand-logo">
            Skin<span className="text-sage-400">sights</span>
          </Link>
          <div className="flex items-center gap-3">
            {user ? (
              <Button onClick={() => navigate("/dashboard")} className="rounded-full bg-sage-400 hover:bg-sage-500 text-white" data-testid="nav-dashboard">
                Dashboard
              </Button>
            ) : (
              <>
                <Link to="/login" className="text-sm text-stone-700 hover:text-stone-900" data-testid="nav-login">Sign in</Link>
                <Button onClick={() => navigate("/register")} className="rounded-full bg-sage-400 hover:bg-sage-500 text-white" data-testid="nav-register">
                  Start tracking
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      <section className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7 animate-fade-up">
          <p className="text-xs uppercase tracking-[0.18em] text-sage-500 mb-6" data-testid="hero-eyebrow">Your skin, gently understood</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-stone-900">
            A quiet space to <em className="text-sage-400 not-italic italic">notice</em><br/>
            how your skin <em className="italic">heals</em>.
          </h1>
          <p className="mt-8 text-lg text-stone-600 max-w-xl leading-relaxed">
            Photograph, log treatments, rate symptoms — and let an AI dermatology assistant help you spot patterns over weeks. Beautifully shareable with your doctor.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Button onClick={() => navigate(user ? "/new" : "/register")} className="rounded-full bg-sage-400 hover:bg-sage-500 text-white px-7 py-6 text-base" data-testid="hero-cta-primary">
              Begin a daily entry
            </Button>
            <Button variant="outline" onClick={() => navigate("/login")} className="rounded-full border-stone-300 px-7 py-6 text-base" data-testid="hero-cta-secondary">
              I already track here
            </Button>
          </div>
          {!user && (
            <div className="mt-5 flex items-center gap-3 flex-wrap" data-testid="hero-google-row">
              <span className="text-xs uppercase tracking-widest text-stone-400">or</span>
              <button
                type="button"
                onClick={handleGoogleLogin}
                data-testid="hero-google-btn"
                className="inline-flex items-center gap-2.5 rounded-full bg-white border border-stone-200 hover:border-stone-300 hover:shadow-sm transition-all px-5 py-2.5 text-sm text-stone-800"
              >
                <svg width="16" height="16" viewBox="0 0 18 18" aria-hidden="true">
                  <path fill="#4285F4" d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                </svg>
                Continue with Google
              </button>
            </div>
          )}
        </div>

        <div className="lg:col-span-5 relative">
          <div className="absolute -top-6 -left-6 w-40 h-40 bg-clay-400/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-8 -right-4 w-56 h-56 bg-sage-200/40 rounded-full blur-3xl" />
          <div className="relative grid grid-cols-2 gap-4">
            <img src="https://images.unsplash.com/photo-1613717883524-b0544792de7e?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" alt="Day 1" className="rounded-2xl object-cover h-64 w-full shadow-lg"/>
            <img src="https://images.unsplash.com/photo-1665700301703-b2b61e089ffe?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" alt="Day 14" className="rounded-2xl object-cover h-64 w-full shadow-lg mt-10"/>
            <div className="col-span-2 glass rounded-2xl p-5">
              <div className="flex items-center justify-between text-xs uppercase tracking-widest text-stone-500">
                <span>Severity trend</span><span>14 days</span>
              </div>
              <svg viewBox="0 0 300 60" className="mt-3 w-full h-14">
                <polyline fill="none" stroke="#89967A" strokeWidth="2.5" strokeLinejoin="round"
                  points="0,40 30,42 60,35 90,30 120,38 150,28 180,22 210,18 240,15 270,12 300,8" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24 grid md:grid-cols-4 gap-6">
        {[
          { icon: CalendarDays, title: "Daily entries", body: "Photos, body region, severity sliders." },
          { icon: Activity, title: "Trend insights", body: "See itching, pain & redness over weeks." },
          { icon: Sparkles, title: "AI observations", body: "Claude vision spots subtle changes." },
          { icon: FileText, title: "Doctor-ready PDF", body: "Export the whole journal in one click." },
        ].map((f, i) => (
          <div key={i} className="bg-white rounded-2xl p-6 border border-stone-100 hover:-translate-y-0.5 transition-all duration-300" data-testid={`feature-card-${i}`}>
            <f.icon className="w-6 h-6 text-sage-400" />
            <h3 className="font-serif text-2xl mt-4 text-stone-900">{f.title}</h3>
            <p className="text-sm text-stone-600 mt-2 leading-relaxed">{f.body}</p>
          </div>
        ))}
      </section>

      <footer className="border-t border-stone-200 py-8 px-6 max-w-7xl mx-auto" data-testid="footer">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-stone-500">
          <span>Skinsights — a personal wellness journal. Not medical advice.</span>
          <div className="flex gap-5">
            <Link to="/privacy" className="hover:text-stone-700" data-testid="footer-privacy">Privacy</Link>
            <Link to="/terms" className="hover:text-stone-700" data-testid="footer-terms">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
